// Customer check-ins: short private multiple-choice question sets.
// One set = one "checkpoint". The start set shows once at the top of a course;
// lesson sets are attached per lesson in admin (Lesson.checkpointSetId).
// Pure and SDK-free; Firestore I/O lives in firebase.ts.

import { QUIZ_SETS } from "./quizzes";

export type CheckpointOption = { id: string; label: string };
export type CheckpointQuestion = {
  id: string;
  text: string;
  options: CheckpointOption[];
  /** Quiz only: the correct option id, and why (shown after submitting). */
  answer?: string;
  explain?: string;
};
export type CheckpointSet = {
  id: string;
  title: string;
  intro: string;
  questions: CheckpointQuestion[];
  /** A quiz shows the score and the right answers after submitting; a check-in just says thanks. */
  quiz?: boolean;
  /** Optional free-text question shown under the choices; never required. */
  noteLabel?: string;
};

export type CheckpointResponse = {
  userId: string;
  courseId: string;
  setId: string;
  /** questionId -> optionId. Missing key = not answered. */
  answers: Record<string, string>;
  note: string;
  skipped: boolean;
  updatedAt: Date | null;
};

const opts = (...labels: string[]): CheckpointOption[] => labels.map((label, i) => ({ id: String(i + 1), label }));

export const CHECKPOINT_SETS: Record<string, CheckpointSet> = {
  ...QUIZ_SETS,
  "amf-start": {
    id: "amf-start",
    title: "ก่อนเริ่มเรียน ขอถาม 4 ข้อสั้นๆ",
    intro: "ใช้เวลาประมาณ 1 นาที คำตอบเห็นแค่ดิว ช่วยให้ดิวปรับคอร์สให้ตรงกับที่คุณต้องการ",
    questions: [
      { id: "stage", text: "ตอนนี้คุณอยู่ในขั้นไหนของการทำ Digital Product?", options: opts(
        "ยังไม่มีไอเดียสินค้า",
        "มีไอเดียแล้ว แต่ยังไม่ได้สร้าง",
        "มีสินค้าแล้ว แต่ยังไม่มีระบบขาย",
        "มีระบบขายแล้ว แต่ยังขายไม่ได้หรือขายน้อย",
        "ขายได้แล้ว และต้องการขยายยอดขาย") },
      { id: "goal", text: "เป้าหมายหลักที่ซื้อคอร์สนี้คืออะไร?", options: opts(
        "หาไอเดียและสร้างสินค้าแรก",
        "สร้างเว็บไซต์และระบบขายอัตโนมัติ",
        "ทำ Content เพื่อหาลูกค้า",
        "เริ่มยิง Ads เพื่อขายสินค้า",
        "ปรับปรุงหรือขยายธุรกิจที่ขายอยู่แล้ว") },
      { id: "blocker", text: "อะไรคืออุปสรรคใหญ่ที่สุดในตอนนี้?", options: opts(
        "ไม่รู้จะขายอะไร",
        "ไม่มั่นใจว่าสินค้ามีคนซื้อ",
        "สร้างเว็บไซต์หรือระบบไม่เป็น",
        "ไม่รู้จะหาลูกค้ายังไง",
        "มีคนสนใจแต่ยังปิดการขายไม่ได้",
        "ไม่มีเวลา / ทำหลายอย่างไม่ทัน") },
      { id: "income", text: "ตอนนี้มีรายได้จาก Digital Product ของตัวเองประมาณเท่าไรต่อเดือน?", options: opts(
        "ยังไม่มีรายได้",
        "ต่ำกว่า 5,000 บาท",
        "5,000–20,000 บาท",
        "20,001–100,000 บาท",
        "มากกว่า 100,000 บาท",
        "ไม่สะดวกตอบ") },
    ],
  },
  "amf-a": {
    id: "amf-a",
    title: "เช็คพอยต์: สินค้าและระบบขาย",
    intro: "ตอบหรือข้ามก็ได้ ช่วยให้ดิวรู้ว่าตรงไหนที่คนติด",
    questions: [
      { id: "built", text: "ตอนนี้สร้างอะไรสำเร็จแล้ว?", options: opts(
        "ยังไม่ได้เริ่ม",
        "มีสินค้าแล้ว",
        "มี Landing Page แล้ว",
        "มีระบบรับเงินและส่งสินค้าแล้ว",
        "ทดลองขายจริงแล้ว") },
    ],
    noteLabel: "ถ้ายังไปต่อไม่ได้ ติดอะไรที่สุด?",
  },
  "amf-b": {
    id: "amf-b",
    title: "เช็คพอยต์: คอนเทนต์และการหาลูกค้า",
    intro: "ตอบหรือข้ามก็ได้ ช่วยให้ดิวรู้ว่าตรงไหนที่คนติด",
    questions: [
      { id: "content", text: "ได้ทดลองเผยแพร่ Content เพื่อขายสินค้าจริงหรือยัง?", options: opts(
        "ยังไม่ได้ทำ",
        "ทำแล้วแต่ยังไม่มีคนสนใจ",
        "มีคนสนใจหรือทักมา",
        "มีคนเข้าหน้าขาย",
        "ได้ยอดขายแล้ว") },
    ],
    noteLabel: "ตอนนี้อะไรทำให้การหาลูกค้ายากที่สุด?",
  },
  "amf-c": {
    id: "amf-c",
    title: "เช็คพอยต์: ยอดขายและก้าวต่อไป",
    intro: "ตอบหรือข้ามก็ได้ ช่วยให้ดิวรู้ว่าคุณต้องการอะไรต่อ",
    questions: [
      { id: "status", text: "ตอนนี้ธุรกิจ Digital Product ของคุณอยู่ในสถานะไหน?", options: opts(
        "ยังไม่ได้เปิดขาย",
        "เปิดขายแล้วแต่ยังไม่มีลูกค้า",
        "มีลูกค้าคนแรกแล้ว",
        "ขายต่อเนื่องได้แล้ว",
        "กำลังทดลองขยายยอดขาย") },
    ],
    noteLabel: "ถ้าจะให้ธุรกิจไปต่อได้ ตอนนี้ต้องการความช่วยเหลือเรื่องใดมากที่สุด?",
  },
};

/** courseId -> start set shown once at the top of the course. */
export const START_SET_BY_COURSE: Record<string, string> = {
  "ai-sales-system": "amf-start",
};

export const NOTE_MAX = 1000;

/** Correct answers in a quiz (0 for a check-in, which has no answers). */
export function quizScore(set: CheckpointSet, answers: Record<string, string>): number {
  return set.questions.filter((q) => q.answer && answers[q.id] === q.answer).length;
}

/** Answers that belong to the set: known question, known option. Drops anything else. */
export function cleanAnswers(set: CheckpointSet, answers: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const q of set.questions) {
    const a = answers[q.id];
    if (a && q.options.some((o) => o.id === a)) out[q.id] = a;
  }
  return out;
}

export type QuestionSummary = {
  questionId: string;
  text: string;
  answered: number;
  rows: { optionId: string; label: string; count: number; pct: number }[];
};

/** Count answers per option for one set. Skipped responses and other sets are ignored. */
export function summarizeSet(set: CheckpointSet, responses: CheckpointResponse[]): QuestionSummary[] {
  const mine = responses.filter((r) => r.setId === set.id && !r.skipped);
  return set.questions.map((q) => {
    const counts = new Map<string, number>();
    for (const r of mine) {
      const a = r.answers[q.id];
      if (a) counts.set(a, (counts.get(a) ?? 0) + 1);
    }
    const answered = [...counts.values()].reduce((s, n) => s + n, 0);
    return {
      questionId: q.id,
      text: q.text,
      answered,
      rows: q.options.map((o) => {
        const count = counts.get(o.id) ?? 0;
        return { optionId: o.id, label: o.label, count, pct: answered ? Math.round((count / answered) * 100) : 0 };
      }),
    };
  });
}

/** Why an edited quiz can't be saved yet ("" = fine). Max 20 questions: firestore.rules caps saved answers at 20. */
export function quizProblem(set: CheckpointSet): string {
  if (!set.title.trim()) return "ใส่ชื่อ Quiz ก่อน";
  if (set.questions.length === 0) return "ต้องมีอย่างน้อย 1 ข้อ";
  if (set.questions.length > 20) return "ได้ไม่เกิน 20 ข้อ";
  for (const [i, q] of set.questions.entries()) {
    const n = `ข้อ ${i + 1}`;
    if (!q.text.trim()) return `${n}: ยังไม่มีคำถาม`;
    if (q.options.filter((o) => o.label.trim()).length < 2) return `${n}: ต้องมีอย่างน้อย 2 ตัวเลือก`;
    if (set.quiz && !q.options.some((o) => o.id === q.answer && o.label.trim())) return `${n}: ยังไม่ได้เลือกคำตอบที่ถูก`;
  }
  return "";
}
